'use client';

/**
 * @fileoverview Live `/event/[slug]` composer for `EventSpineTemplate` (WEB-726).
 *
 * The spine itself is a slot-based server-friendly View. This connected
 * island owns the auth boundary, save / RSVP mutations, `?action=` deep
 * links, and the organisms the spine replaced only in geometry — social,
 * nearby, related, trust footer — so those shipped features do not silently
 * disappear at cutover.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useNotification } from '@loci/store';
import {
  useGetEventUserStateQuery,
  useRsvpEventMutation,
  useSaveEventMutation,
  useUnrsvpEventMutation,
  useUnsaveEventMutation,
} from '@loci/api';
import type { CityCode } from '@loci/city-editions';
import { useTheme } from '@loci/ui';

import { EventFigure } from '@/components/molecules/EventFigure';
import { normalizeMeridiem } from '@/lib/format/normalizeMeridiem';
import { SaveEventButton } from '@/components/molecules/SaveEventButton';
import { TrustFooter } from '@/components/molecules/TrustFooter';
import { EventTitleBlock } from '@/components/molecules/EventTitleBlock';
import { DetailsUnconfirmedBadge } from '@/components/molecules/DetailsUnconfirmedBadge';
import { EventLiveStatusToast } from '@/components/organisms/EventDetail';
import { EventDecisionBar } from '@/components/organisms/EventDecisionPanel';
import { EventPanelComposer } from '@/components/organisms/EventPanelComposer';
import { createEventModuleRenderer } from '@/components/organisms/EventModules/registry';
import type { GettingThereOption } from '@/components/organisms/GettingThereCard';
import {
  NearbyRecommendationsCard,
  type NearbyItem,
} from '@/components/organisms/NearbyRecommendationsCard';
import { RelatedShelf } from '@/components/organisms/RelatedShelf';
import {
  SocialDualStateCard,
  type SocialDualStateAuthData,
} from '@/components/organisms/SocialDualStateCard';
import { EventSpineTemplate } from '@/components/templates/EventSpineTemplate/EventSpineTemplate';
import { useAuth } from '@/contexts/AuthContext';
import { usePersonalization } from '@/hooks/usePersonalization';
import { useDiscoverHref } from '@/hooks/useDiscoverHref';
import {
  trackExternalLinkClick,
  trackRecommendationRsvp,
  trackRecommendationSave,
} from '@/lib/analytics';
import { crossHostSignup } from '@/lib/cross-host-redirect';
import { cityUrl } from '@/lib/cross-host-urls';
import {
  buildNearbyTabs,
  buildRelatedShelfProps,
  buildSocialAuthData,
  resaleVerdictOf,
  sourceFeedLabelOf,
} from '@/lib/eventDetail/carriedAdapters';
import { isEventSocialCardEnabled } from '@/lib/eventDetail/eventSocialCardEnabled';
import { readLocationVenueSlug } from '@/lib/eventDetail/readResolvablePageSlug';
import type {
  EventDetailCategory,
  EventDetailV2,
  EventNearbyResponse,
  EventRelatedResponse,
  EventSocialResponse,
} from '@/types/eventDetail';
import { getCategoryChroma } from '@/utils/categoryColors';

const EVENT_DETAIL_SURFACE = 'event-detail';
const VENUE_FALLBACK_NAME = 'Venue TBA';

export interface EventSpineConnectedProps {
  event: EventDetailV2;
  social: EventSocialResponse | null;
  nearby: EventNearbyResponse | null;
  related: EventRelatedResponse | null;
  slug: string;
  cityCode: CityCode;
  cityLabel: string;
  initialAction?: 'save' | 'rsvp' | null;
  placesGettingThereOptionsPromise?: Promise<readonly GettingThereOption[] | null>;
}

interface SpineVenueLocation {
  name?: string | null;
  timezone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
}

function primaryCategoryOf(event: EventDetailV2): EventDetailCategory | null {
  return (event.categories?.[0] ?? null) as EventDetailCategory | null;
}

function venueNameOf(event: EventDetailV2): { name: string; isFallback: boolean } {
  const location = (event.location ?? null) as SpineVenueLocation | null;
  const name = location?.name;
  return typeof name === 'string' && name.trim().length > 0
    ? { name: name.trim(), isFallback: false }
    : { name: VENUE_FALLBACK_NAME, isFallback: true };
}

function timezoneOf(event: EventDetailV2): string {
  const location = (event.location ?? null) as SpineVenueLocation | null;
  return location?.timezone ?? event.timezone ?? 'America/New_York';
}

function standfirstDetailOf(event: EventDetailV2): string | undefined {
  const fromMeta = event.meta?.ageRestriction;
  if (typeof fromMeta === 'string' && fromMeta.trim().length > 0) return fromMeta.trim();
  const fromEvent = event.ageRestriction;
  if (typeof fromEvent === 'string' && fromEvent.trim().length > 0) return fromEvent.trim();
  return undefined;
}

/**
 * EventSpineConnected — the live-route composer. Server fetchers stay on the
 * page; this island fills the spine slots and carries the organisms the
 * spine does not replace.
 */
export function EventSpineConnected({
  event,
  social,
  nearby,
  related,
  slug,
  cityCode,
  cityLabel,
  initialAction = null,
  placesGettingThereOptionsPromise,
}: EventSpineConnectedProps): ReactElement {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { setUserId, getSessionId } = usePersonalization();
  const { mode } = useTheme();
  const theme = mode === 'dark' ? 'dark' : 'light';
  const [saveEventMutation] = useSaveEventMutation();
  const [unsaveEventMutation] = useUnsaveEventMutation();
  const [rsvpEventMutation, { isLoading: isRsvpLoading }] = useRsvpEventMutation();
  const [unrsvpEventMutation, { isLoading: isUnrsvpLoading }] = useUnrsvpEventMutation();
  const isRsvpPending = isRsvpLoading || isUnrsvpLoading;
  const { showNotification } = useNotification();
  const actionHandled = useRef(false);
  const rsvpActionHandled = useRef(false);

  const [isRsvpd, setIsRsvpd] = useState<boolean>(event.atGlance.userIsGoing);
  const [goingCount, setGoingCount] = useState<number>(event.atGlance.goingCount);

  const { data: eventUserState } = useGetEventUserStateQuery(
    user?.id ? { eventId: event.id } : skipToken,
  );
  const isSaved = isAuthenticated ? (eventUserState?.isSaved ?? false) : false;

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      setUserId(user.id);
    }
  }, [isAuthenticated, user?.id, setUserId]);

  const handleSaveToggle = useCallback(
    (nextSaved: boolean) => {
      if (!user?.id) return;
      const sessionId = getSessionId();
      const action = nextSaved ? 'save' : 'unsave';
      const run = async () => {
        try {
          if (action === 'save') {
            await saveEventMutation({ userId: user.id, eventId: event.id, sessionId }).unwrap();
            trackRecommendationSave(event.id, undefined, undefined, {
              surface: EVENT_DETAIL_SURFACE,
            });
          } else {
            await unsaveEventMutation({ userId: user.id, eventId: event.id, sessionId }).unwrap();
          }
        } catch {
          showNotification({
            type: 'error',
            title: action === 'save' ? 'Failed to save event' : 'Failed to remove saved event',
          });
        }
      };
      void run();
    },
    [user?.id, getSessionId, saveEventMutation, unsaveEventMutation, showNotification, event.id],
  );

  const handleCtaActivate = useCallback(() => {
    const href = event.decisionPanel?.cta.href;
    if (href) {
      trackExternalLinkClick(event.id, href, 'tickets');
    }
  }, [event.decisionPanel?.cta.href, event.id]);

  const handleRequireAuthForSave = useCallback(() => {
    const target = crossHostSignup(`/event/${slug}?action=save`);
    window.location.assign(target);
  }, [slug]);

  useEffect(() => {
    if (initialAction !== 'save' || actionHandled.current || isLoading) return;
    actionHandled.current = true;

    if (isAuthenticated && user?.id) {
      const runSave = async () => {
        try {
          await saveEventMutation({
            userId: user.id,
            eventId: event.id,
            sessionId: getSessionId(),
          }).unwrap();
          trackRecommendationSave(event.id, undefined, undefined, {
            surface: EVENT_DETAIL_SURFACE,
          });
          showNotification({ type: 'success', title: 'Event saved!' });
        } catch {
          showNotification({ type: 'error', title: 'Failed to save event' });
        }
      };
      void runSave();
    } else if (!isAuthenticated) {
      const target = crossHostSignup(`/event/${slug}?action=save`);
      window.location.assign(target);
    }
  }, [
    initialAction,
    isAuthenticated,
    isLoading,
    user?.id,
    event.id,
    slug,
    saveEventMutation,
    getSessionId,
    showNotification,
  ]);

  useEffect(() => {
    if (initialAction !== 'rsvp' || rsvpActionHandled.current || isLoading) return;
    rsvpActionHandled.current = true;

    if (isAuthenticated && user?.id) {
      if (event.atGlance.userIsGoing) {
        showNotification({ type: 'success', title: "You're going!" });
        return;
      }
      setIsRsvpd(true);
      setGoingCount((count) => count + 1);
      const runRsvp = async () => {
        try {
          await rsvpEventMutation({ eventId: event.id }).unwrap();
          trackRecommendationRsvp(event.id, undefined, undefined, {
            surface: EVENT_DETAIL_SURFACE,
          });
          showNotification({ type: 'success', title: "You're going!" });
        } catch {
          setIsRsvpd(false);
          setGoingCount((count) => Math.max(0, count - 1));
          showNotification({ type: 'error', title: 'Failed to RSVP' });
        }
      };
      void runRsvp();
    } else if (!isAuthenticated) {
      const target = crossHostSignup(`/event/${slug}?action=rsvp`);
      window.location.assign(target);
    }
  }, [
    initialAction,
    isAuthenticated,
    isLoading,
    user?.id,
    event.id,
    event.atGlance.userIsGoing,
    slug,
    rsvpEventMutation,
    showNotification,
  ]);

  useEffect(() => {
    setIsRsvpd(event.atGlance.userIsGoing);
    setGoingCount(event.atGlance.goingCount);
  }, [event.atGlance.userIsGoing, event.atGlance.goingCount]);

  const handleRsvpToggle = useCallback(() => {
    if (!isAuthenticated || !user?.id) {
      const target = crossHostSignup(`/event/${slug}?action=rsvp`);
      window.location.assign(target);
      return;
    }

    const nextRsvpd = !isRsvpd;
    const prevRsvpd = isRsvpd;
    const prevGoingCount = goingCount;

    setIsRsvpd(nextRsvpd);
    setGoingCount((count) => Math.max(0, count + (nextRsvpd ? 1 : -1)));

    const run = async () => {
      try {
        if (nextRsvpd) {
          await rsvpEventMutation({ eventId: event.id }).unwrap();
          trackRecommendationRsvp(event.id, undefined, undefined, {
            surface: EVENT_DETAIL_SURFACE,
          });
        } else {
          await unrsvpEventMutation({ eventId: event.id }).unwrap();
        }
      } catch {
        setIsRsvpd(prevRsvpd);
        setGoingCount(prevGoingCount);
        showNotification({
          type: 'error',
          title: nextRsvpd ? 'Failed to RSVP' : 'Failed to remove RSVP',
        });
      }
    };
    void run();
  }, [
    isAuthenticated,
    user?.id,
    slug,
    isRsvpd,
    goingCount,
    event.id,
    rsvpEventMutation,
    unrsvpEventMutation,
    showNotification,
  ]);

  const primaryCategory = primaryCategoryOf(event);
  // WEB-1231 / R25: the uncategorised "See all" keeps a signed-in viewer on `app.*`.
  const toDiscover = useDiscoverHref(cityCode);
  const categorySlug = primaryCategory?.slug ?? null;
  const chroma = getCategoryChroma(categorySlug ?? '', theme);
  const venue = venueNameOf(event);
  const venueName = venue.name;
  const venueSlug = readLocationVenueSlug(event.location);
  const venueHref =
    !venue.isFallback && venueSlug !== null
      ? cityUrl(cityCode, `/venue/${encodeURIComponent(venueSlug)}`)
      : undefined;
  const location = (event.location ?? null) as SpineVenueLocation | null;
  const sourceFeedLabel = sourceFeedLabelOf(event, venueName);
  // WEB-1098 — `true` | `false` | `null` (unknown). The marker renders only on
  // a positive verdict; the trust footer withholds primary-only claims on
  // `null` without asserting resale.
  const resaleVerdict = resaleVerdictOf(event);

  const socialAuthData = useMemo<SocialDualStateAuthData | null>(
    () => (social ? buildSocialAuthData(social) : null),
    [social],
  );

  const nearbyTabs = useMemo(() => buildNearbyTabs(nearby, location), [nearby, location]);
  const hasNearbyTabs =
    nearbyTabs.eat.length > 0 || nearbyTabs.drink.length > 0 || nearbyTabs.stay.length > 0;

  const relatedShelfProps = useMemo(
    () =>
      buildRelatedShelfProps({
        related,
        primaryCategory,
        cityCode,
        timezone: timezoneOf(event),
        toDiscover,
      }),
    [related, primaryCategory, cityCode, event, toDiscover],
  );

  const doorsChip = event.decisionPanel
    ? {
        kicker: event.decisionPanel.kicker,
        headline:
          event.decisionPanel.headline == null
            ? event.decisionPanel.headline
            : normalizeMeridiem(event.decisionPanel.headline),
      }
    : null;

  return (
    <div data-testid="event-spine-connected">
      <EventLiveStatusToast initialStatus={event.status ?? null} />

      <EventSpineTemplate
        event={event}
        pageSlug={slug}
        cityCode={cityCode}
        cityLabel={cityLabel}
        categorySlug={categorySlug}
        titleBlock={
          <EventTitleBlock
            categorySlug={categorySlug}
            theme={theme}
            title={event.title}
            startsAt={event.date}
            timezone={timezoneOf(event)}
            startTimeKnown={event.startTimeKnown}
            venueName={venueName}
            venueHref={venueHref}
            standfirstDetail={standfirstDetailOf(event)}
            decisionPanel={doorsChip}
            resaleListing={resaleVerdict === true}
          />
        }
        figure={
          event.imageUrl || event.imageUrls ? (
            <EventFigure imageUrl={event.imageUrl} imageUrls={event.imageUrls} />
          ) : undefined
        }
        panel={
          <EventPanelComposer
            event={event}
            categorySlug={categorySlug}
            cityCode={cityCode}
            placesGettingThereOptionsPromise={placesGettingThereOptionsPromise}
            onCtaActivate={handleCtaActivate}
          />
        }
        tabletBar={
          <EventDecisionBar
            event={event}
            variant="bar"
            categorySlug={categorySlug}
            onCtaActivate={handleCtaActivate}
          />
        }
        mobileBar={
          <EventDecisionBar
            event={event}
            variant="bottom-bar"
            categorySlug={categorySlug}
            onCtaActivate={handleCtaActivate}
          />
        }
        renderModule={createEventModuleRenderer({ event, chroma })}
        afterEditorial={
          <>
            <DetailsUnconfirmedBadge qualityStatus={event.qualityStatus} variant="block" />
            <div className="flex flex-col gap-4">
              <SaveEventButton
                isSaved={isSaved}
                eventTitle={event.title}
                isAuthenticated={isAuthenticated}
                onToggle={handleSaveToggle}
                onRequireAuth={handleRequireAuthForSave}
              />
              {isEventSocialCardEnabled() ? (
                <SocialDualStateCard
                  eventPath={`/event/${slug}`}
                  totalGoingCount={goingCount}
                  socialData={socialAuthData}
                  isRsvpd={isRsvpd}
                  isRsvpPending={isRsvpPending}
                  onRsvpToggle={handleRsvpToggle}
                />
              ) : null}
            </div>
            {hasNearbyTabs ? (
              <NearbyRecommendationsCard
                tabs={nearbyTabs as { eat: NearbyItem[]; drink: NearbyItem[]; stay: NearbyItem[] }}
                eventId={event.id}
              />
            ) : null}
            <TrustFooter sourceFeedLabel={sourceFeedLabel} resaleListing={resaleVerdict} />
          </>
        }
        afterGrid={
          <RelatedShelf
            events={relatedShelfProps.events}
            totalInCategoryCount={relatedShelfProps.totalInCategoryCount}
            categoryLabel={relatedShelfProps.categoryLabel}
            seeAllHref={relatedShelfProps.seeAllHref}
          />
        }
      />
    </div>
  );
}
